import { Injectable, Injector } from '@angular/core';

import { FormField, SubscriberService } from '@services';
import { BaseField, ReportBaseClass, ReportClass } from '@modules/reporting/models/types/base';
import { CheckDetailModelService } from '@modules/reporting/models/types/checkDetail/service';
import { CheckDetailService } from '@services/checkDetail/check-detail.service';
import { DataToIncludeFormComponent } from './../../../components/data-to-include-form/data-to-include-form.component';
import { SelectFiltersFormComponent } from '@modules/reporting/components';
import { ICheck, IResponse } from '@modules/management/pages/details/check/models';

import { fromEvent, interval } from 'rxjs';
import { debounce, map } from 'rxjs/operators';
import {
  cloneDeep,
  difference,
  each,
  filter,
  find,
  get,
  includes,
  isEmpty,
  merge,
  orderBy,
  reject,
  some,
  split,
  values,
  map as _map,
  toString as _toString
} from 'lodash';

export interface IGroupItem extends ICheck {
  id: string,
  text: string,
}

export interface IGroupedList {
  text: string,
  children: IGroupItem[],
}

@Injectable()
export class checkDetailReport extends ReportBaseClass {

  public dataToIncludeFields: any[] = [];
  public reportType = ReportClass.CheckDetail;

  private checkDetailService: CheckDetailService = this.injector.get(CheckDetailService);
  private checkDetailModelService: CheckDetailModelService = this.injector.get(CheckDetailModelService);
  public reportColumnOptions = this.checkDetailModelService.getColumns();
  public reportFieldOptions = this.checkDetailModelService.getFieldOptions();
  private subscriber: SubscriberService = this.injector.get(SubscriberService);

  filtersFields = [
    this.baseFields[BaseField.CheckStatus],
    this.baseFields[BaseField.CheckResults],
    this.baseFields[BaseField.CheckType],
    this.baseFields[BaseField.DeploymentType],
    this.baseFields[BaseField.DeploymentName],
    this.baseFields[BaseField.TargetLocations],
    this.baseFields[BaseField.TargetZones],
    this.baseFields[BaseField.TargetAssets],
    this.baseFields[BaseField.TargetTeams],
    this.baseFields[BaseField.TargetPermissions],
    this.baseFields[BaseField.TargetUsers],
    this.baseFields[BaseField.Users],
    this.baseFields[BaseField.Responders],
    this.baseFields[BaseField.Permissions],
    this.baseFields[BaseField.Groups],
    this.baseFields[BaseField.Assigned],
    this.baseFields[BaseField.AssignedTeam],
    this.baseFields[BaseField.AssignedPerms],
    {
      containerClass: 'report-field check checkDetail',
      title: this.translate.instant('SHARED.Detail_Tags'),
      name: 'detailTags',
      type: 'selectmenu',
      placeholder: this.translate.instant('REPORTING.EDIT_Any_Tag'),
      multiple: true,
      canDelete: true,
      valueProperty: 'tagID',
      options: this.settingsService.customTags.data,
      onRemoveField: (field: any) => this.onRemoveField(field),
      func: (item: any) => item.tag,
    },
    {
      containerClass: 'report-field check checkDetail',
      title: this.translate.instant('SHARED.Question_Category'),
      name: 'questionCategory',
      type: 'selectmenu',
      placeholder: this.translate.instant('SHARED.Any_Category'),
      multiple: true,
      canDelete: true,
      valueProperty: 'id',
      options: this.getQuestionCategory(),
      onRemoveField: (field: any) => this.onRemoveField(field),
      func: (item: any) => item.description,
    },
  ];
  public fields = [
    this.baseFields[BaseField.Timespan],
    this.baseFields[BaseField.LocationTime],
    this.baseFields[BaseField.ScheduledTime],
    {
      containerClass: 'custom-data-field report-field checkDetail',
      title: this.translate.instant('REPORTING.EDIT_Data_to_Include'),
      name: 'checkDetailReportColumns',
      type: 'customElement',
      required: true,
      component: DataToIncludeFormComponent,
      inputs: {
        options: cloneDeep(this.reportColumnOptions)
      },
      outputs: {
        onChanged: (selectedItems) => {
          const field = <FormField>find(this.formConfig.fields, {name: 'checkDetailReportColumns'});
          (field.componentRef as DataToIncludeFormComponent).selectedItems = selectedItems;
          this.dataToIncludeFields = selectedItems;
          this.updateHideEmptyQuestionsVisibility();
        }
      },
      options: cloneDeep(this.reportColumnOptions)
    },
    this.baseFields[BaseField.IncludingAll],
    {
      containerClass: 'report-field checkDetail',
      title: this.translate.instant('REPORTING.EDIT_Hide_Unanswered_Questions'),
      name: 'hideEmptyQuestions',
      type: 'checkbox',
      role: 'none',
      class: 'inline-checkboxes',
      multiple: false,
      options: [
        {
          id: 'hide',
          description: this.translate.instant('REPORTING.EDIT_Hide_Unanswered_Questions')
        }
      ]
    },
    {
      containerClass: 'report-field obsdets observation check checkDetail ccsFormSubdivider',
      title: this.translate.instant('REPORTING.EDIT_Report_Data_Selection'),
      type: 'divider',
    },
    {
      containerClass: 'custom-data-field report-field check checkDetail',
      title: this.translate.instant('SHARED.Select_Filters'),
      name: 'filtersFields',
      type: 'customElement',
      component: SelectFiltersFormComponent,
      inputs: {
        options: cloneDeep(this.filtersFields),
        selectedItems: []
      },
      outputs: {
        onChanged: (selectedItems) => {
          const field = <FormField>find(this.formConfig.fields, {name: 'filtersFields'});
          (field.componentRef as SelectFiltersFormComponent).selectedItems = selectedItems;
          this.removedFilters = difference(this.filters, selectedItems);
          this.filters = selectedItems;
          this.showFilters();
        }
      },
      options: cloneDeep(this.filtersFields)
    },
    {
      containerClass: 'report-field obsdets checkDetail',
      title: this.translate.instant('SHARED.Check_Name'),
      name: 'checkDetailName',
      type: 'selectmenu',
      multiple: false,
      required: true,
      canDelete: false,
      valueProperty: 'checkID',
      placeholder: this.translate.instant('SHARED.Choose_a_check'),
      options: this.getGroupedChecksByGroups(),
      searchable: true,
      func: (ref: any) => ref.title,
      test: (ref) => this.isFieldDisabledValid(ref),
      onRemoveField: (field: any) => this.onRemoveField(field),
      onChange: (selections) => this.setOptionsByChecks([selections])
    },
  ];
  private resps: any;
  private opts: any;

  constructor(protected injector: Injector) {
    super(injector);
    setTimeout(() => this.updateHideEmptyQuestionsVisibility(), 0);
  }

  public findInterval(opts?, items?) {
    return {};
  }

  private readonly QUESTION_COLUMN_IDS = ['answerDetails', 'answerDetailsDocumentation', 'questionCategory', 'detailTags'];

  private updateHideEmptyQuestionsVisibility() {
    // Only the report edit page attaches a form. The report view page and the check-detail
    // questions component construct this report without one, and the constructor's deferred
    // call still fires there, so there is no container to update.
    if (!this.formConfig) {
      return;
    }
    const hasQuestionColumns = some(this.dataToIncludeFields, (item) => {
      const id = typeof item === 'string' ? item : item?.id;
      return includes(this.QUESTION_COLUMN_IDS, id);
    });
    const container = $(`#${this.formConfig.prefix}hideEmptyQuestions_container`);
    if (hasQuestionColumns) {
      container.show();
    } else {
      container.hide();
    }
  }

  public init(reportData) {
    if (reportData.checkType.length) {
      this.defineCheckVisibilityByValue(reportData.checkType);
    }
    if (reportData.checkDetailName) {
      this.setOptionsByChecks([reportData.checkDetailName]);
    }

    const checkDetailReportColumns: any = find(this.formConfig.fields, {name: 'checkDetailReportColumns'});
    checkDetailReportColumns.inputs.reportID = this.messageID;

    this.dataToIncludeFields = get(reportData, 'selectors.extraColumns') || [];
    this.updateHideEmptyQuestionsVisibility();

    super.init(reportData);
  }

  public async report(theTable, theChart, report, reportId?: number) {
    this.reportData = report;
    const tableDef = merge({}, this.tableDef, {
      onRowClicked: async (currentRow, event) => {
        const response = currentRow?.response;

        if ($(event.target).hasClass('col-link')) {
          const questionId = $(event.target).closest('.link').attr('data-question-id');
          this.openDetails(response, questionId, opts);
        } else {
          const responseID = response?.responseID;
          if (responseID) {
            this.router.navigate([`pages/dashboard/check-detail/${responseID}`], {queryParams: {skipFooter: true}})
          }
        }
      },
      initComplete: () => {
        const searchElement = $(theTable).closest('.dataTables_wrapper').find(`input[type='search']`);
        searchElement.off();

        fromEvent(searchElement, 'input')
          .pipe(
            debounce(() => interval(700)),
            map((event: any) => event?.target?.value)
          ).subscribe( async val => {
            // ponytail: table may have been destroyed/rebuilt within the 700ms debounce
            // window (CVX-6063) — DataTable() on a non-table element has undefined
            // settings and throws on the draw/sort pass. Bail if it is no longer a table.
            if (!(<any>$.fn.dataTable).isDataTable(theTable)) {
              return;
            }
            await this.loadingService.enable(this.translate.instant('SHARED.Search'));
            $(theTable).DataTable().search(val).draw();
            await this.loadingService.disable();
            searchElement.trigger('focus');
        });
      }
    });
    const opts = cloneDeep(report.selectors);
    const timeByTimespan = this.getTimeByTimespan(opts.timespan, opts.startTime, opts.endTime);
    let data = [];
    opts.startTime = timeByTimespan.startTime;
    opts.endTime = timeByTimespan.endTime;
    const responses = await this.getResponses(opts);
    const columns = this.getColumns(opts.extraColumns, responses, opts);

    data = _map(responses, (response) => {
      const responseData = { response };

      each(columns, (column) => {
        responseData[column.id] = column.func([response]);
      });

      return responseData;
    });

    tableDef.data = data;
    tableDef.columns = this.preparationColumns(columns);

    if (tableDef.columns.length) {
      this.tableService.showDataTable(theTable, tableDef);
    }
    $('.report-view-has-chart').hide();
  }

  private getColumns(columns: string[], responses: IResponse[], opts?: any) {
    const columnsInstances = _map(columns, (column) => {
      return find(this.reportColumnOptions, { id: column });
    });
    const staticColumnsInstances = reject(columnsInstances, 'multipleColumn');
    const multipleColumnsInstances = filter(columnsInstances, 'multipleColumn');
    let targetColumns = staticColumnsInstances;

    if (multipleColumnsInstances.length) {
      const multipleColumns: any[] = this.defineMultipleColumns(multipleColumnsInstances, responses, opts);
      targetColumns.push(...multipleColumns);
    }

    return targetColumns;
  }

  private defineMultipleColumns(columns: any[], responses: IResponse[], opts?: any) {
    const targetColumns = {};

    each(responses, (response) => {
      each(columns, (column) => {
        column.multipleColumnHandler && column.multipleColumnHandler(response, targetColumns);
      });
    });

    // Filter out empty question columns when hideEmptyQuestions is enabled
    if (opts?.hideEmptyQuestions?.length) {
      // Build set of questionIDs that have at least one answer across all responses
      const answeredQuestionIDs = new Set<string>();
      each(responses, (response) => {
        each(Object.keys(response?.answers ?? {}), (qid) => {
          answeredQuestionIDs.add(qid);
        });
      });
      // Filter: for each column, extract questionID from the key and check if answered
      return values(targetColumns).filter((col: any) => {
        // Column key format: '{type}_{questionID}' — use indexOf to handle UUID hyphens
        const underscoreIdx = col.id.indexOf('_');
        if (underscoreIdx === -1) return true; // Safety: keep columns without underscore
        const questionID = col.id.substring(underscoreIdx + 1);
        return answeredQuestionIDs.has(questionID);
      });
    }

    return values(targetColumns);
  }

  private async getResponses(opts) {
    this.opts = cloneDeep(opts);
    // CVX-5929: filter server-side by availableTime (scheduled day) when the
    // scheduled-time option is on, so 2nd-shift responses whose updated_at
    // rolls past midnight still appear on the day they were scheduled.
    this.opts.useUpdateTime = this.useScheduledTime(opts) ? 0 : 1;
    this.resps = await this.checkResponseService.getMatchingCheckResponses(this.opts);
    const filtered = this.filterResponses(opts);

    this.checkResponseService.reportIntervalCache = {
      // CVX-5929: include the scheduled-time mode — it changes which responses
      // appear for the same time range, so it must be part of the cache key.
      cacheKey: `${Number(opts.startTime)}:${Number(opts.endTime)}:${opts.timespan || ''}:${this.useScheduledTime(opts) ? 1 : 0}`,
      result: filtered
    };

    return filtered;
  }

  private filterResponses(opts) {
    const responses = [];
    // CVX-5929: in scheduled-time mode, place each response on its availableTime
    // (scheduled) day. Otherwise fall back to the last-activity chain so a
    // response is bucketed by when it was completed/started/claimed.
    const byScheduledTime = this.useScheduledTime(opts);

    each(this.resps, (ref: any) => {
      let ctime = ref.availableTime * 1000;
      if (!byScheduledTime) {
        if (ref.completionTime) {
          ctime = ref.completionTime * 1000;
        } else if (ref.startTime) {
          ctime = ref.startTime * 1000;
        } else if (ref.claimTime) {
          ctime = ref.claimTime * 1000;
        }
      }

      if (ctime < opts.startTime || ctime > opts.endTime) {
        return;
      }

      if (!ref.locationID) {
        if (!isEmpty(ref.answers)) {
          each(ref.answers, data => {
            ref.locationID = data.locationID;
            return false;
          });
        }
        if (!ref.locationID) {
          const splitSig = split(ref.targetSignature, ':');
          if (splitSig[0] == 'loc') {
            ref.locationID = +splitSig[1];
          } else if (splitSig[0] == 'asset') {
            ref.locationID = this.assetsService.getAssetLocation(+splitSig[1]);
          } else if (splitSig[0] == 'worker') {
            ref.locationID = this.accountsService.getUserLocation(+splitSig[1]);
          }
          if (!ref.locationID) {
            ref.locationID = get(ref, 'targetLocations[0]') || 0;
          }
        }
      }

      if (!ref.locationID) {
        if (!isEmpty(ref.answers)) {
          each(ref.answers, data => {
            ref.locationID = data.locationID;
            return false;
          });
        }
        if (!ref.locationID) {
          ref.locationID = get(ref, 'targetLocations[0]') || 0;
        }
      }

      ref.zoneID = get(ref, 'target.zones[0]') || 0;

      if (!this.checkResponseService.checkResponse(ref, opts)) {
        return;
      }

      responses.push(ref);
    });

    return responses;
  }

  public getQuestionCategory() {
    let categoryField = [];
    const locationId = this.subscriber.locationID();
    const observationTypes: string[] = ['category', 'pi', 'quality'];
    const titleMap = {
      category: this.translate.instant('SHARED.Unsafe_Conditions'),
      pi: this.translate.instant('SHARED.Process_Improvements'),
      quality: this.translate.instant('SHARED.Quality_Issues')
    };

    each(observationTypes, (type) => {
      const categoriesByType: any[] = orderBy(this.settingsService.getSettingSync(type, locationId, true), 'messageTitle');
      categoryField = [
        ...categoryField,
        ..._map(categoriesByType, (category) => {
          const observationType: string = type === 'category' ? 'condition' : type;
          return {
            id: `${observationType}:${category.messageID}`,
            description: `${titleMap[type]} - ${category.messageTitle}`
          };
        })
      ];
    });
    return categoryField;
  }

  public async getTableItems(queryParams: any): Promise<any[]> {
    const cacheKey = `${Number(queryParams.startTime)}:${Number(queryParams.endTime)}:${queryParams.timespan || ''}:${this.useScheduledTime(queryParams) ? 1 : 0}`;
    const cached = this.checkResponseService.reportIntervalCache;
    if (cached && cached.cacheKey === cacheKey) {
      return cached.result;
    }
    const opts = cloneDeep(queryParams);
    return this.getResponses(opts);
  }

  public preparationColumns(columns) {
    return _map(columns, (column) => {
      const columnConfig: any = {
        title: `${column.label} <span class="sort-icon"></span>`,
        exportTitle: `${column.label}`,
        data: column.fromID || column.id,
        render: (data, type, row) => {
          if (includes(['sort', 'export'], type)) {
            return column.func([row.response], type) || data;
          }
          return data;
        }
      };

      return columnConfig;
    });
  }

  public async openDetails(response: IResponse, questionId: string, opts) {
    const answerElement = response?.answers?.[questionId];

    if (get(answerElement, 'observationID')) {
      // ca
      const observation = await this.observationService.getObservation(get(answerElement, 'observationID'));
      const queryParam = {
        superTaskBoard: false,
        headerIsHidden: true
      };
      const dataSet = map(this.checkDetailService.currentTableData, 'observationID');
      // CVX-5916: getObservation resolves null when the observation was archived/removed.
      if (observation) {
        this.observationDetailService.navigateToDetail(observation, dataSet, false, false, queryParam);
      }
    } else if (get(answerElement, 'flaggedIssue') || answerElement?.result) {
      let routerParameters = [`pages/dashboard/issue-detail/${response.responseID}/${get(answerElement, 'flaggedIssue')}`];

      if (!answerElement?.flaggedIssue && answerElement?.result) {
        routerParameters = [`pages/dashboard/view-validation/${response.responseID}/${questionId}`];
      }

      this.router.navigate(routerParameters, {
        queryParams: {
          backNavigation: true,
          footerIsHidden: true
        }
      });
    }
  }

  private getGroupedChecksByGroups() {
    const groupedChecksByGroups: IGroupedList[] = [];
    const checkTypes = this.settingsService.getSettingSync('checkType');

    each(checkTypes, (checkType) => {
      const checksByType = values(this.checksService.getChecksByType([checkType.messageID]));

      if (checksByType.length) {
        const checkListItem = {
          text: checkType.messageTitle,
          children: _map(checksByType, (checkItem: ICheck) => ({
            ...checkItem,
            id: _toString(checkItem.checkID),
            text: checkItem.title,
          }))
        };
        if (checkListItem.children.length) {
          groupedChecksByGroups.push(checkListItem);
        }
      }
    });

    return groupedChecksByGroups;
  }

}
